import React, { useState, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emojiToCountryCode = (emoji) => {
    if (!emoji) return "";
    const codePoints = Array.from(emoji).map(char => char.codePointAt(0));
    const isRegionalIndicator = (cp) => cp >= 0x1F1E6 && cp <= 0x1F1FF;
    
    if (codePoints.length >= 2 && isRegionalIndicator(codePoints[0]) && isRegionalIndicator(codePoints[1])) {
        const char1 = String.fromCharCode(codePoints[0] - 0x1F1E6 + 97);
        const char2 = String.fromCharCode(codePoints[1] - 0x1F1E6 + 97);
        return char1 + char2;
    }
    
    if (emoji.includes("🏴󠁧󠁢󠁳󠁣󠁴󠁿")) return "gb-sct";
    if (emoji.includes("🏴󠁧󠁢󠁥󠁮󠁧󠁿")) return "gb-eng";
    
    return "";
};

const splitEmojiAndName = (teamStr) => {
    const spaceIndex = teamStr.indexOf(" ");
    if (spaceIndex !== -1) {
        const emoji = teamStr.substring(0, spaceIndex);
        const name = teamStr.substring(spaceIndex + 1);
        return [emoji, name];
    }
    return ["", teamStr];
};

const TEAM_FLAGS = {
    "mexique": "mx",
    "mexico": "mx",
    "afrique du sud": "za",
    "south africa": "za",
    "corée du sud": "kr",
    "south korea": "kr",
    "r. tchèque": "cz",
    "rep. tchèque": "cz",
    "czech republic": "cz",
    "czechia": "cz",
    "republique tcheque": "cz",
    "canada": "ca",
    "bosnie": "ba",
    "bosnia": "ba",
    "bosnia & herzegovina": "ba",
    "bosnia and herzegovina": "ba",
    "qatar": "qa",
    "suisse": "ch",
    "switzerland": "ch",
    "brésil": "br",
    "brazil": "br",
    "maroc": "ma",
    "morocco": "ma",
    "haïti": "ht",
    "haiti": "ht",
    "écosse": "gb-sct",
    "scotland": "gb-sct",
    "usa": "us",
    "états-unis": "us",
    "united states": "us",
    "paraguay": "py",
    "australie": "au",
    "australia": "au",
    "turquie": "tr",
    "turkey": "tr",
    "allemagne": "de",
    "germany": "de",
    "curaçao": "cw",
    "curacao": "cw",
    "côte d'ivoire": "ci",
    "côte d’ivoire": "ci",
    "cote d'ivoire": "ci",
    "ivory coast": "ci",
    "équateur": "ec",
    "ecuador": "ec",
    "pays-bas": "nl",
    "netherlands": "nl",
    "japon": "jp",
    "japan": "jp",
    "suède": "se",
    "sweden": "se",
    "tunisie": "tn",
    "tunisia": "tn",
    "belgique": "be",
    "belgium": "be",
    "égypte": "eg",
    "egypt": "eg",
    "iran": "ir",
    "n. zélande": "nz",
    "new zealand": "nz",
    "n. zealand": "nz",
    "espagne": "es",
    "spain": "es",
    "cap-vert": "cv",
    "cape verde": "cv",
    "arabie saoudite": "sa",
    "saudi arabia": "sa",
    "uruguay": "uy",
    "france": "fr",
    "sénégal": "sn",
    "senegal": "sn",
    "norvège": "no",
    "norway": "no",
    "irak": "iq",
    "iraq": "iq",
    "argentine": "ar",
    "argentina": "ar",
    "algérie": "dz",
    "algeria": "dz",
    "autriche": "at",
    "austria": "at",
    "jordanie": "jo",
    "jordan": "jo",
    "portugal": "pt",
    "rd congo": "cd",
    "dem. rep. congo": "cd",
    "dr congo": "cd",
    "democratic republic of congo": "cd",
    "congo dr": "cd",
    "ouzbékistan": "uz",
    "uzbekistan": "uz",
    "colombie": "co",
    "colombia": "co",
    "angleterre": "gb-eng",
    "england": "gb-eng",
    "croatie": "hr",
    "croatia": "hr",
    "ghana": "gh",
    "panama": "pa",
};

const renderTeam = (teamName) => {
    const cleanName = teamName.trim();
    const lowerName = cleanName.toLowerCase();
    
    // 1. Check if it is a country team in our map (render ONLY flag, larger size)
    const countryCode = TEAM_FLAGS[lowerName];
    if (countryCode) {
        return (
            <img 
                src={`https://flagcdn.com/w40/${countryCode}.png`} 
                srcSet={`https://flagcdn.com/w80/${countryCode}.png 2x`}
                width="40"
                alt={cleanName}
                title={cleanName} // Shows country name on hover
                loading="lazy"
                className="rounded-sm shadow-sm flex-shrink-0 border border-[#2A4A6B]/50 hover:scale-110 transition-transform cursor-help"
            />
        );
    }
    
    // 2. Check if the name starts with an emoji (e.g. simulated matches like "🇫🇷 PSG")
    const [emoji, name] = splitEmojiAndName(cleanName);
    const parsedCode = emojiToCountryCode(emoji);
    if (parsedCode) {
        return (
            <span className="inline-flex items-center gap-1.5">
                <img 
                    src={`https://flagcdn.com/w28/${parsedCode}.png`} 
                    srcSet={`https://flagcdn.com/w56/${parsedCode}.png 2x`}
                    width="28"
                    alt={emoji}
                    loading="lazy"
                    className="rounded-sm shadow-sm flex-shrink-0"
                />
                <span>{name}</span>
            </span>
        );
    }
    
    // 3. Fallback: just render the text (club name)
    return <span>{cleanName}</span>;
};

const renderTeamsWithFlags = (teamsStr) => {
    const separators = [" – ", " - ", " vs ", " vs. "];
    let separator = null;
    for (const sep of separators) {
        if (teamsStr.includes(sep)) {
            separator = sep;
            break;
        }
    }
    
    if (separator) {
        const [team1, team2] = teamsStr.split(separator);
        return (
            <span className="inline-flex items-center gap-2">
                {renderTeam(team1)}
                <span className="text-gray-400 font-normal px-0.5">{separator.trim() === "–" ? "–" : "vs"}</span>
                {renderTeam(team2)}
            </span>
        );
    }
    
    return renderTeam(teamsStr);
};

const SIMULATED_MATCHES = [
    { teams: "France – Sénégal", competition: "Mondial", date: "Ce soir · 21h00", odds: { home: 1.35, draw: 4.80, away: 8.50 } },
    { teams: "Espagne – Portugal", competition: "Mondial", date: "Ce soir · 22h00", odds: { home: 2.10, draw: 3.40, away: 3.25 } },
    { teams: "🇫🇷 PSG – Marseille", competition: "Ligue 1", date: "Demain · 21h00", odds: { home: 1.45, draw: 4.80, away: 6.50 } },
    { teams: "Brésil – Argentine", competition: "Mondial", date: "Demain · 02h00", odds: { home: 2.20, draw: 3.25, away: 3.10 } },
    { teams: "Allemagne – Angleterre", competition: "Mondial", date: "Ce soir · 18h30", odds: { home: 2.15, draw: 3.30, away: 3.10 } }
];

export const MatchesTicker = () => {
    const [matches, setMatches] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        const fetchMatches = async () => {
            try {
                const [oddsRes, wcRes] = await Promise.allSettled([
                    axios.get(`${API}/odds`),
                    axios.get(`${API}/worldcup/matches`)
                ]);

                if (active) {
                    // Obtenir la date d'aujourd'hui au format DD/MM
                    const today = new Date();
                    const formatter = new Intl.DateTimeFormat('fr-FR', {
                        timeZone: 'Europe/Paris',
                        day: '2-digit',
                        month: '2-digit'
                    });
                    const parts = formatter.formatToParts(today);
                    let dd = "";
                    let mm = "";
                    for (const part of parts) {
                        if (part.type === 'day') dd = part.value;
                        else if (part.type === 'month') mm = part.value;
                    }
                    const todayStr = `${dd}/${mm}`;
                    
                    let todayMatches = [];

                    // 1. Traiter les matchs de l'API générale des cotes
                    if (oddsRes.status === "fulfilled" && oddsRes.value.data) {
                        const oddsMatches = oddsRes.value.data.filter(
                            (m) => m.kickoff && m.kickoff.includes(todayStr)
                        );
                        todayMatches.push(...oddsMatches);
                    }

                    // 2. Traiter les matchs de la Coupe du Monde
                    if (wcRes.status === "fulfilled" && wcRes.value.data && wcRes.value.data.matches) {
                        const wcMatches = wcRes.value.data.matches.filter((m) => {
                            if (!m.utcDate) return false;
                            const matchDate = new Date(m.utcDate);
                            const mParts = formatter.formatToParts(matchDate);
                            let mDd = "";
                            let mMm = "";
                            for (const part of mParts) {
                                if (part.type === 'day') mDd = part.value;
                                else if (part.type === 'month') mMm = part.value;
                            }
                            return `${mDd}/${mMm}` === todayStr;
                        });

                        // Formater les matchs Coupe du Monde pour correspondre au format du ticker
                        const formattedWcMatches = wcMatches.map((m) => {
                            const matchDate = new Date(m.utcDate);
                            const timeFormatter = new Intl.DateTimeFormat('fr-FR', {
                                timeZone: 'Europe/Paris',
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: false
                            });
                            const timeParts = timeFormatter.formatToParts(matchDate);
                            let hh = "";
                            let min = "";
                            for (const part of timeParts) {
                                if (part.type === 'hour') hh = part.value;
                                else if (part.type === 'minute') min = part.value;
                            }
                            const timeStr = `${hh}h${min}`;

                            return {
                                match_id: `wc-${m.id}`,
                                competition: "Coupe du Monde",
                                home: m.homeTeam?.name || "TBD",
                                away: m.awayTeam?.name || "TBD",
                                kickoff: `Aujourd'hui · ${timeStr}`,
                            };
                        });

                        // Éviter les doublons avec oddsMatches
                        formattedWcMatches.forEach((wcM) => {
                            const exists = todayMatches.some(
                                (existing) =>
                                    (existing.home.toLowerCase() === wcM.home.toLowerCase() &&
                                        existing.away.toLowerCase() === wcM.away.toLowerCase()) ||
                                    (existing.home.toLowerCase() === wcM.away.toLowerCase() &&
                                        existing.away.toLowerCase() === wcM.home.toLowerCase())
                            );
                            if (!exists) {
                                todayMatches.push(wcM);
                            }
                        });
                    }

                    setMatches(todayMatches);
                    setLoading(false);
                }
            } catch (err) {
                if (active) {
                    setLoading(false);
                }
            }
        };

        fetchMatches();
        return () => {
            active = false;
        };
    }, []);

    const renderScrollingContent = () => {
        const hasLiveMatches = matches.length > 0;
        // Si aucun match réel aujourd'hui, on prend les matchs simulés de "Ce soir"
        const matchItems = hasLiveMatches 
            ? matches 
            : SIMULATED_MATCHES.filter(m => m.date && (m.date.includes("Ce soir") || m.date.includes("aujourd'hui")));

        return matchItems.map((m, index) => {
            const isSimulated = !m.match_id;
            const key = isSimulated ? `sim-${index}` : m.match_id;
            const comp = m.competition || "Match";
            const kickoff = m.kickoff || m.date;

            return (
                <span key={key} className="inline-flex items-center text-white font-semibold">
                    <span className="bg-[#00C853]/15 text-[#00C853] border border-[#00C853]/30 text-[10px] font-extrabold px-1.5 py-0.5 rounded uppercase tracking-wider mr-2">
                        {comp}
                    </span>
                    <span className="text-xs font-bold inline-flex items-center gap-1.5">
                        {isSimulated ? (
                            renderTeamsWithFlags(m.teams)
                        ) : (
                            <span className="inline-flex items-center gap-2">
                                {renderTeam(m.home)}
                                <span className="text-gray-400 font-normal px-0.5">vs</span>
                                {renderTeam(m.away)}
                            </span>
                        )}
                    </span>
                    <span className="text-[10px] bg-[#1E3A5F]/50 px-1.5 py-0.5 rounded text-gray-300 ml-1.5 font-medium">{kickoff}</span>
                    <span className="mx-10 inline-flex items-center text-[#2A4A6B] font-light select-none">•</span>
                </span>
            );
        });
    };

    return (
        <div 
            data-testid="matches-ticker"
            className="w-full bg-[#07111D] border-b border-[#1E3A5F] text-white flex flex-col md:flex-row items-stretch select-none"
        >
            {/* Left Brand Badge */}
            <div className="flex-shrink-0 bg-gradient-to-r from-[#00C853] to-[#009624] text-black px-4 py-2 flex items-center justify-center gap-2 font-heading font-black italic shadow-lg z-10">
                <span className="flex h-2 w-2 relative">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-black opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-black"></span>
                </span>
                <span className="text-xs tracking-wider uppercase not-italic font-extrabold">⚽ LES MATCHS DU JOUR :</span>
            </div>

            {/* Scrolling Ticker Section */}
            <div className="flex-1 overflow-hidden relative flex items-center py-2 md:py-0 bg-[#091321]">
                <div 
                    className="animate-marquee flex items-center whitespace-nowrap gap-1"
                    style={{ 
                        animationDuration: `${Math.max(20, (matches.length > 0 ? matches.length : SIMULATED_MATCHES.length) * 8)}s` 
                    }}
                >
                    <div className="flex items-center shrink-0">
                        {renderScrollingContent()}
                    </div>
                    <div className="flex items-center shrink-0">
                        {renderScrollingContent()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MatchesTicker;
